import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lightbulb, Sparkles, Star, Wand2, Trash2 } from "lucide-react";
import {
  analyzeChannelForIdeas,
  generateChannelIdeas,
  saveStoryIdea,
  automateStoryIdea,
  listStoryIdeas,
} from "@/lib/api";
import { useActiveChannelsNav } from "@/hooks/useActiveChannelsNav";

// Each step the user sees while "Generate Story Ideas" is doing its
// background work. We surface them as a tiny progress strip so the
// user knows we didn't freeze on a slow GPT call.
const STEP_DEFS = [
  { key: "analyze_user", label: "Analyze your channel" },
  { key: "analyze_insp", label: "Analyze inspiration channel" },
  { key: "generate", label: "Generate ideas" },
];

export default function GenerateStoryIdeas() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Priority: `?channel=@handle` query param (set when navigating from a
  // scoped Storyboards page) wins over localStorage — otherwise
  // navigating "Storyboards for @evolvedthinker → Generate Ideas"
  // would silently show the WRONG channel from a previous session.
  const initialHandle = (() => {
    const q = (searchParams.get("channel") || "").trim();
    if (q) return q.startsWith("@") ? q : `@${q}`;
    return localStorage.getItem("ytm.idea.lastHandle") || "";
  })();
  const [handle, setHandle] = useState(initialHandle);
  const [inspirationHandle, setInspirationHandle] = useState(
    () => localStorage.getItem("ytm.idea.inspiration") || "",
  );
  const [theme, setTheme] = useState(
    () => localStorage.getItem("ytm.idea.theme") || "",
  );
  const [excludeTopics, setExcludeTopics] = useState(
    () => localStorage.getItem("ytm.idea.excludeTopics") || "",
  );

  const [steps, setSteps] = useState({});  // step.key -> "pending"|"running"|"ok"|"failed"|"skipped"
  const [stepNotes, setStepNotes] = useState({});  // step.key -> detail string
  const [running, setRunning] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [ideasThemeUsed, setIdeasThemeUsed] = useState(null);
  const [ideasInspiration, setIdeasInspiration] = useState(null);
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [savingIdx, setSavingIdx] = useState(null);
  const [automatingIdx, setAutomatingIdx] = useState(null);

  // Titles from the previous generation batch — passed to the backend
  // as `excludeTitles` so "Generate More Ideas" actually returns new
  // ones. Held in a ref so we can populate it AFTER `setIdeas([])`
  // clears the visible list.
  const previousTitlesRef = useRef([]);

  // The user's already-tracked channels — powers the click-to-select
  // picker so the common case (pick one of your own channels) is one
  // click instead of a typed @handle. The manual input is preserved
  // behind a toggle for the "channel I haven't added yet" case.
  const activeChannels = useActiveChannelsNav();
  const [manualEntry, setManualEntry] = useState(false);

  // If the user lands on this page with a pre-set handle that doesn't
  // match any of their tracked channels, open manual entry
  // automatically so they can see + edit the value.
  useEffect(() => {
    if (!handle) return;
    if (activeChannels.length === 0) return;
    const normalized = handle.trim().replace(/^@/, "").toLowerCase();
    const matched = activeChannels.some((c) => {
      const h = (c.handle || "").replace(/^@/, "").toLowerCase();
      return h && h === normalized;
    });
    if (!matched) setManualEntry(true);
  }, [handle, activeChannels]);

  // Persist preferences so they survive refresh.
  useEffect(() => {
    if (handle) localStorage.setItem("ytm.idea.lastHandle", handle);
  }, [handle]);
  useEffect(() => {
    localStorage.setItem("ytm.idea.inspiration", inspirationHandle);
  }, [inspirationHandle]);
  useEffect(() => {
    localStorage.setItem("ytm.idea.theme", theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem("ytm.idea.excludeTopics", excludeTopics);
  }, [excludeTopics]);

  // Pull the user's existing saved-idea titles so the ⭐ Save button
  // immediately greys out for ideas already in the library (idempotent
  // on the backend, but a no-op click is wasted intent).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listStoryIdeas();
        if (cancelled) return;
        const keys = (res?.ideas || [])
          .map((i) => (i.title || "").trim().toLowerCase())
          .filter(Boolean);
        setSavedKeys(new Set(keys));
      } catch {
        // Best-effort — empty set is fine.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setStep = useCallback((key, status, note) => {
    setSteps((s) => ({ ...s, [key]: status }));
    if (note != null) setStepNotes((n) => ({ ...n, [key]: note }));
  }, []);

  const onGenerate = async (e) => {
    e?.preventDefault?.();
    if (running) return;
    const userHandle = (handle || "").trim();
    const inspHandle = (inspirationHandle || "").trim();
    const themeVal = (theme || "").trim();
    if (!userHandle) {
      toast.error("Add your channel handle (e.g. @yourchannel) so the ideas match your tone.");
      return;
    }
    // Snapshot the current batch's titles BEFORE we clear `ideas`, so
    // we can hand them to GPT as `excludeTitles` on the retry — that
    // way "Generate More Ideas" actually returns NEW titles instead
    // of another 10 of the same ones.
    previousTitlesRef.current = (ideas || [])
      .map((it) => (it?.title || "").trim())
      .filter(Boolean);
    setRunning(true);
    setIdeas([]);
    setIdeasThemeUsed(null);
    setIdeasInspiration(null);
    setSteps({});
    setStepNotes({});
    try {
      // --- Step 1 — analyze user's channel (cached server-side)
      setStep("analyze_user", "running", `reading recent uploads of ${userHandle}…`);
      try {
        const a = await analyzeChannelForIdeas(userHandle);
        const isCached = a?.cached ?? a?.from_cache;
        const charCount = (a?.analysis?.characters || []).length;
        setStep("analyze_user", "ok",
          isCached
            ? `using cached analysis · ${charCount} characters`
            : `analyzed ${a?.analysis?.recent_titles?.length || 0} recent videos · ${charCount} characters`,
        );
      } catch (err) {
        const msg = err?.response?.data?.detail || "analyze failed";
        setStep("analyze_user", "failed", msg);
        toast.error(`Couldn't analyze ${userHandle}: ${msg}`);
        setRunning(false);
        return;
      }

      // --- Step 2 — analyze inspiration channel (optional)
      if (inspHandle) {
        setStep("analyze_insp", "running", `reading top videos of ${inspHandle}…`);
        try {
          // We don't need the full analysis for inspiration — the
          // ideas service calls _fetch_inspiration_winners directly.
          // But running analyzeChannelForIdeas warms the same cache
          // and gives the user feedback that the inspiration was
          // recognised.
          const ai = await analyzeChannelForIdeas(inspHandle);
          const isCachedInsp = ai?.cached ?? ai?.from_cache;
          setStep("analyze_insp", "ok",
            isCachedInsp
              ? "using cached inspiration analysis"
              : "inspiration channel analyzed",
          );
        } catch (err) {
          // Inspiration is best-effort — note it but proceed.
          const msg = err?.response?.data?.detail || "analyze failed";
          setStep("analyze_insp", "failed",
            `${msg} — continuing without inspiration`,
          );
        }
      } else {
        setStep("analyze_insp", "skipped", "no inspiration channel provided");
      }

      // --- Step 3 — generate ideas
      setStep("generate", "running", themeVal ? `themed on "${themeVal}"…` : "GPT-5.2 brainstorming…");
      try {
        // If this is a "generate more" click (ideas already showing),
        // send the current batch's titles as `excludeTitles` so GPT
        // doesn't hand back the same 10 titles again. `ideas` still
        // holds the previous batch at this point — we cleared them
        // optimistically at the top of onGenerate only AFTER capturing
        // this list via a ref-like read from the closure? No — we
        // reset state early which loses them. Fix: capture BEFORE
        // clearing (see the small refactor below).
        const res = await generateChannelIdeas(userHandle, {
          excludeTitles: previousTitlesRef.current || [],
          count: 10,
          inspirationHandle: inspHandle || "",
          theme: themeVal,
          topicsToAvoid: (excludeTopics || "")
            .split(/[,\n]/)
            .map((s) => s.trim())
            .filter(Boolean),
        });
        const out = res?.ideas || [];
        setIdeas(out);
        setIdeasThemeUsed(res?.theme || null);
        setIdeasInspiration(res?.inspiration || null);
        setStep("generate", "ok", `${out.length} idea${out.length === 1 ? "" : "s"} ready`);
        toast.success(
          out.length
            ? `${out.length} fresh idea${out.length === 1 ? "" : "s"} — ⭐ Save the ones you want`
            : "Generator returned no ideas — try a different theme",
        );
      } catch (err) {
        const msg = err?.response?.data?.detail || "generate failed";
        setStep("generate", "failed", msg);
        toast.error(msg);
      }
    } finally {
      setRunning(false);
    }
  };

  const handleSave = async (idea, idx) => {
    if (savingIdx === idx) return;
    setSavingIdx(idx);
    try {
      await saveStoryIdea(handle.trim(), idea);
      const key = (idea.title || "").trim().toLowerCase();
      setSavedKeys((s) => new Set([...s, key]));
      toast.success(`Saved "${idea.title}" to Saved Story Ideas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSavingIdx(null);
    }
  };

  const handleAutomate = async (idea, idx) => {
    if (automatingIdx === idx) return;
    setAutomatingIdx(idx);
    try {
      // Save first so the idea exists in the library, then kick off Automate.
      const saved = await saveStoryIdea(handle.trim(), idea);
      const ideaId = saved?.idea_id;
      if (!ideaId) throw new Error("save_failed");
      const res = await automateStoryIdea(ideaId);
      toast.success(res?.status === "queued" ? "Queued — opening Saved Story Ideas to track progress" : "Automate started");
      navigate("/ideas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start Automate");
    } finally {
      setAutomatingIdx(null);
    }
  };

  const handleDismiss = (idx) => {
    // Local-only: the results are ephemeral in-page state — dropping
    // the card is all we need. Re-generating replaces the batch.
    setIdeas((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = useMemo(() => Boolean(handle.trim()) && !running, [handle, running]);

  return (
    <div className="space-y-6" data-testid="generate-story-ideas-page">
      <header className="space-y-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-yellow-400">
          // PHASE 3 · GENERATE STORY IDEAS
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Spin up a fresh batch
        </h1>
        <p className="text-zinc-300 max-w-3xl">
          Drop your channel handle (and optionally an inspiration channel + a
          theme). We&apos;ll analyze both channels in the background and have
          GPT-5.2 draft 10 brand-new story ideas. ⭐ Save the ones worth
          keeping — they land in your{" "}
          <Link to="/ideas" className="text-yellow-300 underline">
            Saved Story Ideas
          </Link>
          {" "}library and Automate is one click away from there.
        </p>
      </header>

      <form
        onSubmit={onGenerate}
        data-testid="generate-form"
        className="space-y-4 border-2 border-zinc-800 bg-zinc-950 rounded-sm p-5"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-yellow-200">
              Your channel
            </span>
            {activeChannels.length > 0 && (
              <button
                type="button"
                onClick={() => setManualEntry((v) => !v)}
                data-testid="generate-handle-mode-toggle"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-yellow-300 transition-colors"
              >
                {manualEntry
                  ? "← pick from my channels"
                  : "type handle manually →"}
              </button>
            )}
          </div>

          {activeChannels.length > 0 && !manualEntry && (
            <div
              data-testid="generate-handle-picker"
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              {activeChannels.map((c) => {
                const normalizedC = (c.handle || "").replace(/^@/, "").toLowerCase();
                const normalizedH = handle.trim().replace(/^@/, "").toLowerCase();
                const isActive = !!normalizedC && normalizedC === normalizedH;
                return (
                  <button
                    key={c.channel_id}
                    type="button"
                    onClick={() => setHandle(c.handle ? `@${c.handle.replace(/^@/, "")}` : "")}
                    disabled={!c.handle}
                    data-testid={`generate-handle-pick-${c.channel_id}`}
                    title={c.handle
                      ? `Use @${c.handle.replace(/^@/, "")}`
                      : "This channel has no @handle on file — refresh it from the Dashboard first."}
                    className={`flex items-center gap-3 text-left px-3 py-2 rounded-sm border-2 transition-colors ${
                      isActive
                        ? "border-yellow-400 bg-yellow-400/10"
                        : "border-zinc-800 hover:border-zinc-600 bg-black"
                    } ${!c.handle ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {c.thumbnail ? (
                      <img
                        src={c.thumbnail}
                        alt=""
                        className="h-8 w-8 rounded-full border border-zinc-800 shrink-0"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-zinc-800 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm truncate ${isActive ? "text-yellow-100 font-semibold" : "text-zinc-100"}`}>
                        {c.name}
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500 truncate">
                        {c.handle ? `@${c.handle.replace(/^@/, "")}` : "no handle on file"}
                      </div>
                    </div>
                    {isActive && (
                      <span className="font-mono text-[10px] font-black text-yellow-400 shrink-0">
                        ●
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {(manualEntry || activeChannels.length === 0) && (
            <label htmlFor="gi-handle" className="block space-y-1.5">
              <input
                id="gi-handle"
                data-testid="generate-handle-input"
                type="text"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="@bellasplayclub"
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
              />
              {activeChannels.length === 0 && (
                <p className="text-[11px] text-zinc-500">
                  Tip: add your channel from{" "}
                  <Link to="/active-channels" className="text-yellow-300 underline">
                    My Channels → Dashboard
                  </Link>
                  {" "}and it&apos;ll show up as a one-click pick here.
                </p>
              )}
            </label>
          )}
        </div>

        <label htmlFor="gi-inspiration" className="block space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">
            Inspiration channel <span className="text-zinc-500 font-normal lowercase tracking-normal">— optional</span>
          </span>
          <input
            id="gi-inspiration"
            data-testid="generate-inspiration-input"
            type="text"
            value={inspirationHandle}
            onChange={(e) => setInspirationHandle(e.target.value)}
            placeholder="@a-bigger-channel-in-your-niche"
            className="w-full bg-black border border-zinc-800 focus:border-cyan-400/60 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
          />
          <p className="text-[11px] text-zinc-400 leading-relaxed max-w-xl">
            We&apos;ll fetch their top videos and let GPT model proven topic
            patterns into ideas for YOUR channel.
          </p>
        </label>

        <label htmlFor="gi-theme" className="block space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-emerald-200">
            Theme for this batch <span className="text-zinc-500 font-normal lowercase tracking-normal">— optional</span>
          </span>
          <input
            id="gi-theme"
            data-testid="generate-theme-input"
            type="text"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="e.g. camping, first day of school, saving money for kids"
            className="w-full bg-black border-2 border-emerald-400/40 focus:border-emerald-400 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
          />
          <p className="text-[11px] text-zinc-400 leading-relaxed max-w-xl">
            Anchor every idea to a specific topic. Leave blank to roam.
          </p>
        </label>

        <label htmlFor="gi-exclude" className="block space-y-1.5">
          <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-red-300">
            Topics to exclude <span className="text-zinc-500 font-normal lowercase tracking-normal">— optional, comma-separated</span>
          </span>
          <input
            id="gi-exclude"
            data-testid="generate-exclude-input"
            type="text"
            value={excludeTopics}
            onChange={(e) => setExcludeTopics(e.target.value)}
            placeholder="e.g. cyclists, pirates, magic wands"
            className="w-full bg-black border-2 border-red-400/40 focus:border-red-400 focus:outline-none rounded-sm px-2.5 py-2 text-sm text-zinc-100 font-mono"
          />
          <p className="text-[11px] text-zinc-400 leading-relaxed max-w-xl">
            Hard block-list — GPT will regenerate any idea that mentions
            these (or synonyms). You can also write exclusions inline in
            the theme box (e.g. <span className="text-zinc-300">&quot;camping, no cyclists&quot;</span>) —
            we pick both up.
          </p>
        </label>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            type="submit"
            data-testid="generate-story-ideas-btn"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 font-black px-5 py-2.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
            {running
              ? "Working…"
              : (ideas.length > 0 ? "Generate More Ideas" : "Generate Story Ideas")}
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {ideas.length > 0
              ? "replaces current batch · GPT-5.2 · ~30–45 s"
              : "10 ideas · GPT-5.2 · ~30–45 s"}
          </span>
        </div>
      </form>

      {/* Progress strip — only renders once a generate has started. */}
      {Object.keys(steps).length > 0 && (
        <ol
          data-testid="generate-progress-steps"
          className="space-y-1.5 border-2 border-zinc-800 bg-zinc-950 rounded-sm p-4"
        >
          {STEP_DEFS.map(({ key, label }) => {
            const st = steps[key] || "pending";
            const note = stepNotes[key];
            return (
              <li
                key={key}
                data-testid={`generate-step-${key}`}
                className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em]"
              >
                <StepGlyph status={st} />
                <span className={[
                  "min-w-[170px] tracking-[0.25em]",
                  st === "ok" ? "text-emerald-200" :
                  st === "running" ? "text-yellow-200" :
                  st === "failed" ? "text-red-300" :
                  st === "skipped" ? "text-zinc-500" : "text-zinc-400",
                ].join(" ")}>{label}</span>
                {note && (
                  <span className="text-zinc-400 normal-case tracking-normal text-[11px]">
                    {note}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {ideasThemeUsed && (
        <div
          data-testid="ideas-theme-chip"
          className="border border-emerald-400/30 bg-emerald-400/5 rounded-sm px-4 py-2 font-mono text-[11px] text-emerald-200 uppercase tracking-[0.2em]"
        >
          // theme · <span className="text-emerald-100 font-bold normal-case tracking-normal">{ideasThemeUsed}</span>
        </div>
      )}

      {ideasInspiration && (
        <div
          data-testid="ideas-inspiration-chip"
          className="border border-cyan-400/30 bg-cyan-400/5 rounded-sm px-4 py-3 space-y-1.5"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
            // INSPIRED BY @{(ideasInspiration.handle || "").replace(/^@/, "")} · {ideasInspiration.winner_count} top videos
          </div>
          {ideasInspiration.name && (
            <div className="text-sm text-zinc-100 font-bold">{ideasInspiration.name}</div>
          )}
        </div>
      )}

      {ideas.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="generated-ideas-grid">
          {ideas.map((idea, i) => {
            const key = (idea.title || "").trim().toLowerCase();
            const saved = savedKeys.has(key);
            return (
              <article
                key={`${idea.title}-${i}`}
                data-testid={`generated-idea-${i}`}
                className="border-2 border-zinc-800 hover:border-yellow-300/60 transition-colors bg-zinc-950 rounded-sm p-3 space-y-2"
              >
                <h3 className="text-white font-bold leading-snug">{idea.title}</h3>
                {idea.hook && <p className="text-zinc-300 text-sm italic">{idea.hook}</p>}
                {idea.summary && <p className="text-zinc-400 text-sm leading-relaxed">{idea.summary}</p>}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => handleSave(idea, i)}
                    disabled={savingIdx === i || saved}
                    data-testid={`generated-idea-save-${i}`}
                    className={[
                      "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-2 px-3 py-1.5 rounded-sm transition-colors",
                      saved
                        ? "border-zinc-700 text-zinc-500 cursor-default"
                        : "border-cyan-400 text-cyan-100 hover:text-black hover:bg-cyan-300 bg-cyan-400/10",
                    ].join(" ")}
                  >
                    <Star className={["w-3 h-3", saved ? "fill-zinc-500" : "fill-cyan-300"].join(" ")} />
                    {savingIdx === i ? "Saving…" : saved ? "Saved" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAutomate(idea, i)}
                    disabled={automatingIdx === i}
                    data-testid={`generated-idea-automate-${i}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 bg-yellow-400/15 font-black px-3 py-1.5 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Save + start building the storyboard now"
                  >
                    {automatingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                    {automatingIdx === i ? "Starting…" : "Save & Automate"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDismiss(i)}
                    data-testid={`generated-idea-dismiss-${i}`}
                    title="Not interested — remove from this batch"
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-red-300 border border-transparent hover:border-red-500/40 px-2.5 py-1.5 rounded-sm transition-colors ml-auto"
                  >
                    <Trash2 className="w-3 h-3" />
                    Dismiss
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!running && !ideas.length && Object.keys(steps).length === 0 && (
        <div
          data-testid="generate-empty-hint"
          className="border-2 border-zinc-800 bg-zinc-950 rounded-sm p-6 font-mono text-sm text-zinc-400 flex items-start gap-3"
        >
          <Sparkles className="w-4 h-4 text-yellow-300 mt-0.5 shrink-0" />
          <p>
            Tip — start with just your channel handle for a broad batch. Add an
            inspiration channel to model on a proven niche, or a theme to lock
            every idea to a topic like &quot;camping&quot;.
          </p>
        </div>
      )}
    </div>
  );
}

function StepGlyph({ status }) {
  if (status === "running") {
    return <Loader2 className="w-3.5 h-3.5 text-yellow-300 animate-spin" />;
  }
  const glyph = {
    ok: "✓",
    failed: "✗",
    skipped: "—",
    pending: "·",
  }[status] || "·";
  const color = {
    ok: "text-emerald-300",
    failed: "text-red-400",
    skipped: "text-zinc-500",
    pending: "text-zinc-600",
  }[status] || "text-zinc-600";
  return (
    <span className={`${color} font-bold w-3.5 h-3.5 inline-flex items-center justify-center`} aria-hidden>
      {glyph}
    </span>
  );
}
